import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import { cn } from '@crowdex/ui/lib/utils';
import { AudioLines, MessagesSquare } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AppHomeColumn } from '@/components/app-home-sidebar';
import { useNeedsYouCount } from '@/components/desktop-shell/desktop-nav-rail';
import { ItemIcon } from '@/components/item-icon';
import { SIDEBAR_SIDE_SHORTCUT } from '@/components/sidebar/shell-shortcuts';
import { useDockedApp, useDockedTabId } from '@/features/apps/app-panel-sidebar';
import { CORE_LIVE_VIEW } from '@/features/plugins/core-panels';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useAppHome } from '@/lib/app-home';
import { coreAppSidebarSlot } from '@/lib/desktop-shell/core-app-sidebar-slot';
import {
  CONVERSATIONS_SIDE,
  resetSidebarSides,
  type SidebarSide,
  useSidebarSide,
} from '@/lib/desktop-shell/sidebar-side';
import { matchesShortcut } from '@/lib/platform';
import {
  commandLabel,
  currentShortcut,
  shortcutAllowedFrom,
  useShortcutOverrides,
} from '@/lib/settings-page/keyboard-shortcuts';

/** One segment of the toggle after "Conversations": what a core tool lists. */
type SidebarSegment = { id: string; title: string };

/** The side of a core tool listing its own items (the browser's tabs, Live). */
const APP_SIDE = 'app';

/**
 * The head of the sidebar's body while the side panel's tab is a core tool listing its own items
 * (the browser's tabs): "Conversations | <tool>". On the tool's side, the conversations that wait
 * on the user show their count on "Conversations". ⇧⌘E goes to the next side.
 */
function SidebarSideToggle({
  panel,
  segments,
  side,
  onSideChange,
  density = 'desktop',
}: {
  panel: PluginPanel;
  segments: readonly SidebarSegment[];
  side: SidebarSide;
  onSideChange: (side: SidebarSide) => void;
  density?: 'desktop' | 'drawer';
}) {
  // Its tooltips show the user's keys.
  useShortcutOverrides();
  const needsYou = useNeedsYouCount();
  const sides = [CONVERSATIONS_SIDE, ...segments.map((segment) => segment.id)];
  const current = useRef({ side, sides, onSideChange });
  current.current = { side, sides, onSideChange };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !shortcutAllowedFrom(event)) return;
      if (!matchesShortcut(event, currentShortcut('sidebar-side') ?? SIDEBAR_SIDE_SHORTCUT)) return;
      event.preventDefault();
      const { side: shown, sides: all, onSideChange: change } = current.current;
      change(all[(all.indexOf(shown) + 1) % all.length] ?? CONVERSATIONS_SIDE);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const keys = commandLabel('sidebar-side', SIDEBAR_SIDE_SHORTCUT);
  const Icon = panel.view === CORE_LIVE_VIEW ? AudioLines : pluginIcon(panel.icon ?? 'app');
  const waiting = side !== CONVERSATIONS_SIDE && needsYou > 0;
  const choose = (value: string) => onSideChange(value);
  // Sized by their names: a long name gives way first.
  const item = cn('min-w-0 flex-auto px-3', density === 'drawer' && 'h-10');
  // The conversations' segment as an icon: the sections' names keep their room.
  const compact = segments.length > 1;
  return (
    <SegmentedControl
      aria-label="Sidebar shows"
      className="flex w-full"
      data-sidebar-side={side}
      onValueChange={choose}
      value={side}
    >
      <SegmentedControlItem
        aria-label={waiting ? `Conversations, ${needsYou} waiting` : 'Conversations'}
        className={compact ? cn(item, 'flex-none') : item}
        // The arrow keys move between the sides and show the one they reach.
        onFocus={() => choose(CONVERSATIONS_SIDE)}
        title={`Conversations (${keys})`}
        value={CONVERSATIONS_SIDE}
      >
        {/* Beside several sections of the app, the conversations are their icon (named). */}
        {compact ? (
          <MessagesSquare aria-hidden className="size-4 shrink-0" />
        ) : (
          <span className="truncate">Conversations</span>
        )}
        {waiting && (
          <span
            aria-hidden
            className="type-caption flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-attention px-1 font-semibold text-background leading-none"
            data-needs-you-count
          >
            {needsYou > 9 ? '9+' : needsYou}
          </span>
        )}
      </SegmentedControlItem>
      {segments.map((segment, index) => (
        <SegmentedControlItem
          aria-label={segment.title}
          className={item}
          key={segment.id}
          onFocus={() => choose(segment.id)}
          title={`${segment.title} (${keys})`}
          value={segment.id}
        >
          {/* The app's icon names the first of its sections; one section only has room for it. */}
          {index === 0 && segments.length === 1 && (
            <ItemIcon className="size-3.5" icon={Icon} src={panel.iconSrc} />
          )}
          <span className="truncate">{segment.title}</span>
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

/** Whether a slot element holds anything (a page or an app portals its navigation into it). */
function useSlotFilled(element: HTMLDivElement | null): boolean {
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (element === null) {
      setFilled(false);
      return;
    }
    const sync = () => setFilled(element.childElementCount > 0);
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(element, { childList: true });
    return () => observer.disconnect();
  }, [element]);
  return filled;
}

/**
 * What the sidebar (desktop) or the drawer (phone) shows besides the assistants: `toggle` heads
 * its body, `navigation` is what shows, and `appShown` says the assistants give way to it.
 *
 * - An app full screen (outside any conversation, `/apps/<plugin>…`) owns the column: "‹
 *   Assistants" at its head, then the app's sections (its items, its `page.sidebars`, the live
 *   sections of its view) — the extensions proposal's "Le cadre". No toggle.
 * - An app docked in a conversation leaves the sidebar to the assistants: its sections are a
 *   picker in its tab's bar (`PluginFrame`).
 * - A core tool listing its own items (the browser's tabs, Live) keeps "Conversations | <tool>"
 *   at the head (⇧⌘E goes to the other side), on Conversations until the user picks it; another
 *   tab, a closed panel or a library page over it go back to the conversations. Its list stays
 *   mounted (hidden) on the conversations side, so that it keeps its state.
 */
export function useAppSidebarSide(
  covered: boolean,
  density: 'desktop' | 'drawer' = 'desktop',
): { toggle: ReactNode; navigation: ReactNode; appShown: boolean } {
  const workbench = useWorkbench();
  const home = useAppHome();
  const docked = useDockedApp();
  const tabId = useDockedTabId();
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const slotRef = useCallback((element: HTMLDivElement | null) => {
    coreAppSidebarSlot.set(element);
    setSlot(element);
  }, []);
  const slotFilled = useSlotFilled(slot);
  const homePanel =
    home === null
      ? null
      : (railPanels(workbench).find(
          (panel) =>
            panel.plugin === home.plugin &&
            (panel.appView !== undefined || panel.remoteView !== undefined),
        ) ?? null);
  // A core tool lists its own items through the slot; an extension never draws in the sidebar.
  const tool = home === null && !covered && docked?.hostView !== undefined ? docked : null;
  const segments: SidebarSegment[] =
    tool !== null && slotFilled ? [{ id: APP_SIDE, title: tool.title }] : [];
  const key = tool === null ? null : (tabId ?? tool.view);
  const [chosen, setSide] = useSidebarSide(segments.length === 0 ? null : key, CONVERSATIONS_SIDE);
  // Nothing to show beside the conversations: the next tool starts on them again.
  useEffect(() => {
    if (key === null) resetSidebarSides();
  }, [key]);
  const side = segments.some((segment) => segment.id === chosen) ? chosen : CONVERSATIONS_SIDE;
  const hidden = side === CONVERSATIONS_SIDE;
  if (home !== null)
    return {
      toggle: null,
      navigation: <AppHomeColumn density={density} panel={homePanel} plugin={home.plugin} />,
      appShown: true,
    };
  return {
    toggle:
      tool !== null && segments.length > 0 ? (
        <SidebarSideToggle
          density={density}
          onSideChange={setSide}
          panel={tool}
          segments={segments}
          side={side}
        />
      ) : null,
    navigation: tool !== null && <div hidden={hidden} ref={slotRef} />,
    appShown: segments.length > 0 && !hidden,
  };
}
